// Copyright (c) 2019-present Ladifire, Inc. All Rights Reserved.
// See LICENSE.txt for license information.
import React, {Component} from 'react';
import PropTypes from 'prop-types';
import styled, {ThemeProvider} from 'styled-components';
import exenv from 'exenv';

import {ThemeModes, LfThemeProps} from '../types';
import * as colors from '../colors';

import {CHANNEL, DEFAULT_THEME_MODE} from '../constants';

// For forward-compat until everything is upgraded.
import Theme from './theme';

function getStylesheetResetCSS(state: LfThemeProps) {
    const backgroundColor = colors.background(state);
    return `
    body { background: ${backgroundColor}; }
  `;
}

interface Props {
    children: React.ReactNode;
    mode: ThemeModes;
}

function buildThemeState(mode: ThemeModes): LfThemeProps {
    return {theme: {[CHANNEL]: {mode}}};
}

const LegacyReset = styled.div`
  background-color: ${colors.background};
  color: ${colors.text};

  a {
    color: ${colors.link};
  }
  a:hover {
    color: ${colors.linkHover};
  }
  a:active {
    color: ${colors.linkActive};
  }
  a:focus {
    outline-color: ${colors.linkOutline};
  }
  h1 {
    color: ${colors.heading};
  }
  h2 {
    color: ${colors.heading};
  }
  h3 {
    color: ${colors.heading};
  }
  h4 {
    color: ${colors.heading};
  }
  h5 {
    color: ${colors.heading};
  }
  h6 {
    color: ${colors.subtleHeading};
  }
  small {
    color: ${colors.subtleText};
  }
`;

export default class LfThemeProvider extends Component<
    Props,
    LfThemeProps
    > {
    stylesheet: any;

    static defaultProps = {
        mode: DEFAULT_THEME_MODE,
    };

    static childContextTypes = {
        hasLfThemeProvider: PropTypes.bool,
    };

    static contextTypes = {
        hasLfThemeProvider: PropTypes.bool,
    };

    constructor(props: Props) {
        super(props);
        this.state = buildThemeState(props.mode);
    }

    getChildContext() {
        return {hasLfThemeProvider: true};
    }

    UNSAFE_componentWillMount() {
        if (!this.context.hasLfThemeProvider && exenv.canUseDOM) {
            const css = getStylesheetResetCSS(this.state);
            this.stylesheet = document.createElement('style');
            this.stylesheet.type = 'text/css';
            this.stylesheet.innerHTML = css;
            if (document && document.head) {
                document.head.appendChild(this.stylesheet);
            }
        }
    }

    UNSAFE_componentWillReceiveProps(newProps: Props) {
        if (newProps.mode !== this.props.mode) {
            const newThemeState = buildThemeState(newProps.mode);
            if (this.stylesheet) {
                const css = getStylesheetResetCSS(newThemeState);
                this.stylesheet.innerHTML = css;
            }
            this.setState(newThemeState);
        }
    }

    componentWillUnmount() {
        if (this.stylesheet && document && document.head) {
            document.head.removeChild(this.stylesheet);
            delete this.stylesheet;
        }
    }

    render() {
        const {children} = this.props;
        const {theme} = this.state;
        return (

            /* Wrapping the new provider around the old one provides forward
            compatibility when using the old provider for styled components. This
            allows us to use components converted to use the new API with consumers
            using the old provider along side components that may still be using the
            old theming API. */
            <Theme.Provider value={() => ({mode: theme[CHANNEL].mode})}>
                <ThemeProvider theme={theme}>
                    <LegacyReset>{children}</LegacyReset>
                </ThemeProvider>
            </Theme.Provider>
        );
    }
}
